import _, { chain } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import LocalStorageService from "../../../../../services/local/localStorageService/LocalStorageService";
import { ITrainingModuleInfo } from "../../../../../services/hubs/ProjectDashboardHub/ProjectDashboard_types";
import TrainingModulesApi, {
  TrainingModulesApi as ITrainingModulesApi
} from "../../../../../services/api/v2/trainingModules/TrainingModules.api";
import { PROGRESS_STATUS_OPTIONS, RAG_STATUS_OPTIONS } from "../../../../../constants";
import I18n from "../../../../../core/localization/I18n";

export class TrainingModulesReportingModel {
  trainingModuleApi: ITrainingModulesApi;
  trainingModuleIds: number[];
  @observable moduleActionProgressAndRagStatusChartData: any[] = [];
  @observable trainingModuleActionProgressStatus: any[] = [];
  @observable trainingModuleActionRagStatus: any[] = [];
  projectId: number;
  organisationId: number;
  @observable isLoading: boolean = true;
  @observable filterJSON: any = {};
  @observable ownerChartData: Array<Record<string, any>> = [];
  @observable trainingProgressData: Array<Record<string, any>> = [];
  navigate: Function;
  page: string;
  localStorage = LocalStorageService;

  @computed get hasFilters(): boolean {
    var res = false;
    _.forEach(this.filterJSON, (e: any) => {
      res = res || e.length > 0;
    });

    return res;
  }

  constructor(organisationId: number, projectId: number, navigate: Function, page: string) {
    makeObservable(this);
    this.organisationId = organisationId;
    this.projectId = projectId;
    this.navigate = navigate;
    this.page = page;
    this.trainingModuleApi = TrainingModulesApi;

    let filters = this.localStorage.get(this.getStorageKey(organisationId, projectId));
    if (filters) {
      this.setFilterJSON(JSON.parse(filters));
    }
  }

  onMount() {
    // Load data or perform actions needed when the component mounts
    this.loadData();
  }

  onUnmount() {
    // Cleanup actions when the component unmounts
  }
  @action
  setFilterJSON = (filterJSON: any) => {
    this.filterJSON = filterJSON;
    this.localStorage.set(this.getStorageKey(this.organisationId, this.projectId), JSON.stringify(filterJSON));
  };
  getStorageKey = (organisationId, projectId) => {
    return `modules-reports-charts-${organisationId}-${projectId}`;
  };

  @action
  setFilterJSONFromList = (filterJSON: any[]) => {
    let s = {};

    filterJSON.forEach(e => {
      s[e.key] = e.ids;
    });
    this.setFilterJSON(s);
  };

  resetLocalStorage = () => {
    this.localStorage.remove(this.getStorageKey(this.organisationId, this.projectId));
  };

  async loadData() {
    this.setIsLoading(true);
    await this.getTrainingModule();
    await this.loadTrainingModuleActionProgressAndRagStatusChartData();
    this.setIsLoading(false);
  }

  @action
  getTrainingModule = async () => {
    const trainingModules = await this.trainingModuleApi.getGridData(this.organisationId, this.projectId);
    try {
      this.setTrainingModuleIds(
        trainingModules.payload.map(tm => {
          return tm.id;
        })
      );
      const modules = trainingModules.payload as ITrainingModuleInfo[];
      this.setOwnerChartData(modules);
      this.setTrainingProgressData(modules);
    } catch (error) {
      console.error("Failed to load data", error);
    }
  };

  @action
  setTrainingModuleIds = async trainingModuleIds => {
    this.trainingModuleIds = trainingModuleIds;
  };

  @action
  loadTrainingModuleActionProgressAndRagStatusChartData = async () => {
    let moduleActionProgressStatus = [];
    let moduleActionRagStatus = [];

    const res = await this.trainingModuleApi.getTrainingModuleActionRagStatusAndProgressStatus(
      this.organisationId,
      this.projectId,
      this.trainingModuleIds,
      this.filterJSON.trainingCourseIds
    );

    res.payload.forEach(tm => {
      const module = {
        trainingModule: tm.trainingModuleName
      };
      module["Not Started"] = 0;
      module["In Progress"] = 0;
      module["Completed"] = 0;
      module["actions"] = [];

      tm.actions.forEach(action => {
        const label = PROGRESS_STATUS_OPTIONS.find(p => p.key === action.progressStatus)?.label;
        if (label) {
          module[I18n.t(label)] += 1;
        }

        module["actions"].push({
          refNumber: action.refNumber,
          action: action.name,
          progressStatus: action.progressStatus
        });
      });

      moduleActionProgressStatus.push(module);
    });

    res.payload.forEach(tm => {
      const module = {
        trainingModule: tm.trainingModuleName
      };

      RAG_STATUS_OPTIONS.forEach(rag => {
        module[I18n.t(rag.label)] = 0;
      });

      module["Completed"] = 0;
      module["Unknown"] = 0;
      module["actions"] = [];

      tm.actions.forEach(action => {
        if (action.progressStatus === 3) {
          module["Completed"] += 1;
        } else {
          const ragLabel = RAG_STATUS_OPTIONS.find(p => p.key === action.ragStatus)?.label;
          if (ragLabel) {
            module[I18n.t(ragLabel)] += 1;
          } else {
            module["Unknown"] += 1;
          }
        }
        module["actions"].push({
          refNumber: action.refNumber,
          action: action.name,
          progressStatus: action.progressStatus,
          ragStatus: action.ragStatus
        });
      });

      moduleActionRagStatus.push(module);
    });

    this.setActionProgressStatus(moduleActionProgressStatus);
    this.setActionRagStatus(moduleActionRagStatus);
  };

  @action
  setActionProgressStatus = async data => {
    this.trainingModuleActionProgressStatus = data;
  };

  @action
  setActionRagStatus = async data => {
    this.trainingModuleActionRagStatus = data;
  };

  @action
  setModuleActionProgressAndRagStatusChartData = async data => {
    this.moduleActionProgressAndRagStatusChartData = data;
  };

  @action
  setIsLoading = (isLoading: boolean) => {
    this.isLoading = isLoading;
  };

  @action
  setOwnerChartData(modules: ITrainingModuleInfo[]) {
    this.ownerChartData = chain(modules)
      .groupBy(m => m.owner)
      .omit("")
      .toPairs()
      .map(([owner, items]) => ({ label: owner, count: items.length }))
      .value();
  }

  @action
  setTrainingProgressData(modules: ITrainingModuleInfo[]) {
    this.trainingProgressData = chain(modules)
      .groupBy(m => m.trainingProgress)
      .omit("null")
      .toPairs()
      .map(([progress, items]) => ({ label: progress, count: items.length }))
      .value();
  }
}
